/**
 * PHARMAKON — barcodes · Code 128, subset C (ISO/IEC 15417), in-house encoder (OP-PLAN-03: no
 * dependency). Used for the Pharmakon lot codes (D-15: numeric, 12 digits → 6 data symbols, fits a
 * 40 × 25 mm thermal label, D-16) by the SPA label sheet and the server PDFs.
 *
 * Symbol = quiet zone · START C (105) · one symbol per digit pair (value 00…99) · check symbol ·
 * STOP (106) · quiet zone. Check value = (105 + Σ position × value) mod 103, positions from 1.
 * Every symbol is 3 bars + 3 spaces = 11 modules; STOP is 4 bars + 3 spaces = 13 modules.
 * Test vectors: packages/shared/barcode/__tests__/barcode.test.js (cross-checked against the
 * independent python-barcode 0.16.1 encoder, module strings pasted verbatim).
 *
 * Canonical source: packages/shared/barcode/ (copy in apps/server/src/utils/barcode/, see gs1.js).
 */

/**
 * Bar / space widths of the 107 Code 128 symbols, in modules, starting with a bar (ISO/IEC 15417
 * Table 1). Index = symbol value; 103/104/105 = START A/B/C, 106 = STOP.
 */
export const CODE128_LARGEURS = Object.freeze([
  "212222", "222122", "222221", "121223", "121322", "131222", "122213", "122312", "132212", "221213",
  "221312", "231212", "112232", "122132", "122231", "113222", "123122", "123221", "223211", "221132",
  "221231", "213212", "223112", "312131", "311222", "321122", "321221", "312212", "322112", "322211",
  "212123", "212321", "232121", "111323", "131123", "131321", "112313", "132113", "132311", "211313",
  "231113", "231311", "112133", "112331", "132131", "113123", "113321", "133121", "313121", "211331",
  "231131", "213113", "213311", "213131", "311123", "311321", "331121", "312113", "312311", "332111",
  "314111", "221411", "431111", "111224", "111422", "121124", "121421", "141122", "141221", "112214",
  "112412", "122114", "122411", "142112", "142211", "241211", "221114", "413111", "241112", "134111",
  "111242", "121142", "121241", "114212", "124112", "124211", "411212", "421112", "421211", "212141",
  "214121", "412121", "111143", "111341", "131141", "114113", "114311", "411113", "411311", "113141",
  "114131", "311141", "411131", "211412", "211214", "211232", "2331112",
]);

export const CODE128_START_C = 105;
export const CODE128_STOP = 106;
export const CODE128_MODULO = 103;
/** Minimum quiet zone on each side, in modules (ISO/IEC 15417 §4.4.3: 10 X). */
export const CODE128_ZONE_CALME = 10;

/**
 * Symbol values of a subset C Code 128: START C, the digit pairs, the check value, STOP.
 *
 * @param {string} chiffres an even, non-zero number of digits
 * @returns {number[]}
 */
export function valeursCode128C(chiffres) {
  if (typeof chiffres !== "string" || !/^(?:\d\d)+$/.test(chiffres)) {
    const e = new TypeError(`Code 128 C : un nombre pair de chiffres est requis (${String(chiffres)})`);
    e.code = "CODE128_C_INVALIDE";
    throw e;
  }
  const valeurs = [CODE128_START_C];
  let somme = CODE128_START_C;
  for (let i = 0; i < chiffres.length; i += 2) {
    const v = Number(chiffres.slice(i, i + 2));
    valeurs.push(v);
    somme += v * (i / 2 + 1);
  }
  valeurs.push(somme % CODE128_MODULO, CODE128_STOP);
  return valeurs;
}

/**
 * The bar / space widths of the whole symbol (quiet zones excluded), bar first.
 *
 * @param {string} chiffres
 * @returns {number[]}
 */
export function largeursCode128C(chiffres) {
  const out = [];
  for (const v of valeursCode128C(chiffres)) for (const c of CODE128_LARGEURS[v]) out.push(c.charCodeAt(0) - 48);
  return out;
}

/**
 * The module string of the symbol, quiet zones excluded: "1" = bar module, "0" = space module.
 *
 * @param {string} chiffres
 * @returns {string}
 */
export function modulesCode128C(chiffres) {
  let s = "";
  let barre = true;
  for (const w of largeursCode128C(chiffres)) {
    s += (barre ? "1" : "0").repeat(w);
    barre = !barre;
  }
  return s;
}

function echapperXml(t) {
  return String(t).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
}

/**
 * The symbol as a standalone SVG (black bars on a white background, crisp edges, sized in user
 * units = modules; the caller scales it with CSS or the PDF engine).
 *
 * @param {string} chiffres
 * @param {{ hauteur?: number, zoneCalme?: number, texte?: string|false, taillePolice?: number }} [opts]
 *   hauteur: bar height in modules (default 40); zoneCalme: modules each side (default 10, never
 *   less); texte: human-readable line under the bars (default the digits; false = none)
 * @returns {string}
 */
export function svgCode128C(chiffres, { hauteur = 40, zoneCalme = CODE128_ZONE_CALME, texte, taillePolice = 10 } = {}) {
  const largeurs = largeursCode128C(chiffres);
  const zc = Math.max(CODE128_ZONE_CALME, zoneCalme);
  const lisible = texte === false ? null : (texte ?? chiffres);
  const largeurTotale = largeurs.reduce((a, b) => a + b, 0) + 2 * zc;
  const hauteurTotale = hauteur + (lisible ? taillePolice + 2 : 0);
  const barres = [];
  let x = zc;
  largeurs.forEach((w, i) => {
    if (i % 2 === 0) barres.push(`<rect x="${x}" y="0" width="${w}" height="${hauteur}"/>`);
    x += w;
  });
  const legende = lisible
    ? `<text x="${largeurTotale / 2}" y="${hauteur + taillePolice}" font-family="monospace" font-size="${taillePolice}" text-anchor="middle">${echapperXml(lisible)}</text>`
    : "";
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${largeurTotale} ${hauteurTotale}" ` +
    `width="${largeurTotale}" height="${hauteurTotale}" shape-rendering="crispEdges" role="img" ` +
    `aria-label="Code 128 ${echapperXml(chiffres)}">` +
    `<rect width="${largeurTotale}" height="${hauteurTotale}" fill="#fff"/>` +
    `<g fill="#000">${barres.join("")}${legende}</g></svg>`
  );
}
